import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Sparkles, FileText, Newspaper, Linkedin, Search, Image as ImageIcon,
  Loader2, CheckCircle2, XCircle, Archive, Edit3, RefreshCw, Eye, Play,
  TrendingUp, Bot, Send, AlertTriangle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({}); // Deprecated: auth via HttpOnly cookies

const STATUS_BADGE = {
  draft:          { label: "Brouillon",  c: "bg-slate-100 text-slate-700 border-slate-300" },
  pending_review: { label: "À valider",   c: "bg-amber-100 text-amber-900 border-amber-300" },
  approved:       { label: "Validé",      c: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  rejected:       { label: "Refusé",      c: "bg-rose-100 text-rose-900 border-rose-300" },
  archived:       { label: "Archivé",     c: "bg-slate-200 text-slate-600 border-slate-300" },
  published:      { label: "Publié",      c: "bg-emerald-50 text-emerald-900 border-emerald-200" },
};

const AGENT_LABEL = {
  juridical_watch: { label: "Veille juridique",  icon: Newspaper, color: "text-violet-700" },
  linkedin_acq:    { label: "LinkedIn",          icon: Linkedin,  color: "text-[#0B3A2A]" },
  seo:             { label: "SEO",               icon: Search,    color: "text-emerald-700" },
};

const VISUAL_TYPES = [
  { key: "illustration",    label: "Illustration" },
  { key: "infographic",     label: "Infographie" },
  { key: "quote_card",      label: "Citation" },
  { key: "carousel_slide",  label: "Carrousel" },
];

const TABS = [
  { key: "overview",  label: "Vue d'ensemble", icon: TrendingUp },
  { key: "all",       label: "Tous les contenus", icon: FileText },
  { key: "watch",     label: "Veille", icon: Newspaper, filter: { agent: "juridical_watch" } },
  { key: "linkedin",  label: "LinkedIn", icon: Linkedin, filter: { agent: "linkedin_acq" } },
  { key: "seo",       label: "SEO", icon: Search, filter: { agent: "seo" } },
  { key: "agents",    label: "Agents", icon: Bot },
];

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminAgentsIA() {
  const [tab, setTab] = useState("overview");
  const [statusFilter, setStatusFilter] = useState("");
  const [contents, setContents] = useState([]);
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // { id, title, body }
  const [busyId, setBusyId] = useState(null);
  const [imgBust, setImgBust] = useState({}); // cache-busting des images
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [showManualPost, setShowManualPost] = useState(false);
  const [manualPost, setManualPost] = useState({
    content_type: "linkedin_post", title: "", body: "", theme: "", hashtags: "", auto_approve: true,
  });
  const [showOrgInput, setShowOrgInput] = useState(false);
  const [orgInput, setOrgInput] = useState("");

  const loadLinkedinStatus = useCallback(async () => {
    try {
      const r = await apiClient.get(`/admin/linkedin/status`);
      setLinkedinStatus(r.data);
    } catch {
      setLinkedinStatus(null);
    }
  }, []);

  // Affiche un toast au retour du callback OAuth (?linkedin=connected|error)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const li = params.get("linkedin");
    if (li === "connected") {
      toast.success("LinkedIn connecté ✓");
      // Nettoie l'URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (li === "error") {
      toast.error(`Connexion LinkedIn échouée : ${params.get("msg") || "erreur inconnue"}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const meta = TABS.find((t) => t.key === tab);
      const params = { limit: 50 };
      if (meta?.filter?.agent) params.agent = meta.filter.agent;
      if (statusFilter) params.status = statusFilter;
      const [c, s, a] = await Promise.all([
        apiClient.get(`/admin/contents`, { params }),
        apiClient.get(`/admin/contents/stats`),
        apiClient.get(`/admin/agents`),
      ]);
      setContents(c.data.items || []);
      setStats(s.data);
      setAgents(a.data.agents || a.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadLinkedinStatus(); }, [loadLinkedinStatus]);

  const connectLinkedIn = async () => {
    setBusyId("linkedin:connect");
    try {
      const r = await apiClient.get(`/admin/linkedin/connect`);
      if (r.data?.authorize_url) {
        window.location.href = r.data.authorize_url;
      } else {
        toast.error("URL d'autorisation indisponible.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connexion impossible.");
    } finally { setBusyId(null); }
  };

  const disconnectLinkedIn = async () => {
    if (!window.confirm("Déconnecter LinkedIn ? Vous devrez vous reconnecter pour publier.")) return;
    setBusyId("linkedin:disconnect");
    try {
      await apiClient.post(`/admin/linkedin/disconnect`, {});
      toast.success("LinkedIn déconnecté.");
      loadLinkedinStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Déconnexion impossible.");
    } finally { setBusyId(null); }
  };

  const publishLinkedIn = async (id, target = "organization") => {
    if (!linkedinStatus?.connected) {
      toast.error("Connectez LinkedIn d'abord.");
      return;
    }
    if (!window.confirm(`Publier sur ${target === "organization" ? "la Page CONFILEX" : "votre profil"} ? Cette action est immédiate.`)) return;
    setBusyId(id);
    try {
      const r = await apiClient.post(`/admin/contents/${id}/publish-linkedin`,
        { target, with_image: true, visibility: "PUBLIC" });
      toast.success("Publié sur LinkedIn ✓");
      if (r.data?.post_url) {
        window.open(r.data.post_url, "_blank", "noopener");
      }
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publication impossible.");
    } finally { setBusyId(null); }
  };

  const saveOrgId = async () => {
    if (!/^\d+$/.test(orgInput.trim())) {
      toast.error("L'Organization ID doit être uniquement numérique (ex : 108234567).");
      return;
    }
    setBusyId("linkedin:org");
    try {
      await apiClient.post(`/admin/linkedin/settings/organization`,
        { organization_id: orgInput.trim() });
      toast.success("Organization ID enregistré.");
      setShowOrgInput(false);
      setOrgInput("");
      loadLinkedinStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sauvegarde impossible.");
    } finally { setBusyId(null); }
  };

  const submitManualPost = async () => {
    if (!manualPost.title.trim() || !manualPost.body.trim()) {
      toast.error("Titre et corps du post requis.");
      return;
    }
    setBusyId("manual:post");
    try {
      const tags = (manualPost.hashtags || "").split(/[\s,]+/).map((t) => t.replace(/^#/, "").trim()).filter(Boolean);
      await apiClient.post(`/admin/contents`,
        {
          agent: "linkedin_acq",
          content_type: manualPost.content_type,
          title: manualPost.title,
          body: manualPost.body,
          theme: manualPost.theme || null,
          hashtags: tags,
          auto_approve: manualPost.auto_approve,
        });
      toast.success(manualPost.auto_approve
        ? "Post créé et validé ✓ — prêt à publier"
        : "Brouillon créé ✓");
      setShowManualPost(false);
      setManualPost({ content_type: "linkedin_post", title: "", body: "", theme: "", hashtags: "", auto_approve: true });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible.");
    } finally { setBusyId(null); }
  };

  const runAgent = async (name) => {
    setBusyId(`agent:${name}`);
    try {
      const r = await apiClient.post(`/admin/agents/${name}/run`, {});
      const result = r.data?.result;
      if (result?.skipped) toast.info(`${name} : ${result.reason} (${result.tip || ""})`);
      else toast.success(`${name} exécuté ✓`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec exécution."); }
    finally { setBusyId(null); }
  };

  const toggleAgent = async (name, enabled) => {
    setBusyId(`agent:${name}`);
    try {
      await apiClient.post(`/admin/agents/${name}/${enabled ? "disable" : "enable"}`, {});
      toast.success(enabled ? `${name} désactivé` : `${name} activé`);
      loadAll();
    } catch (e) { toast.error("Action impossible."); }
    finally { setBusyId(null); }
  };

  const doAction = async (id, action, payload = {}) => {
    setBusyId(id);
    try {
      const r = await apiClient.post(`/admin/contents/${id}/${action}`, payload);
      toast.success({
        validate: "Validé ✓",
        reject:   "Refusé",
        archive:  "Archivé",
        visual:   "Visuel généré ✓",
        "regenerate-image": "Nouveau visuel ✓",
        regenerate: "Nouveau brouillon créé",
      }[action] || "Action effectuée");
      if (action === "visual" || action === "regenerate-image") {
        setImgBust((m) => ({ ...m, [id]: Date.now() }));
      }
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible.");
    } finally { setBusyId(null); }
  };

  const savePatch = async () => {
    if (!editing) return;
    setBusyId(editing.id);
    try {
      await apiClient.patch(`/admin/contents/${editing.id}`, {
        title: editing.title,
        body: editing.body,
      });
      toast.success("Modifications enregistrées (v+1)");
      setEditing(null);
      loadAll();
    } catch (e) { toast.error("Sauvegarde impossible."); }
    finally { setBusyId(null); }
  };

  // Optim : calculs dérivés mémorisés pour éviter les recomputes inutiles à chaque render
  const last7dTotal = useMemo(
    () => Object.values(stats?.last_7d_by_status || {}).reduce((a, b) => a + b, 0),
    [stats?.last_7d_by_status],
  );
  const byAgentEntries = useMemo(() => {
    const entries = Object.entries(stats?.by_agent || {});
    const max = entries.length ? Math.max(...entries.map(([, n]) => n)) : 1;
    return entries.map(([k, n]) => ({ k, n, pct: Math.round((n / max) * 100) }));
  }, [stats?.by_agent]);
  const visibleAgents = useMemo(
    () => agents.filter((a) => ["juridical_watch", "linkedin_acq", "seo"].includes(a.name)),
    [agents],
  );

  // ---- VIEWS ----
  const renderOverview = () => (
    <div className="space-y-6" data-testid="overview-section">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500">Total contenus</div>
          <div className="font-serif text-3xl mt-1">{stats?.total ?? 0}</div>
        </Card>
        <Card className="p-4 border-amber-200">
          <div className="text-xs uppercase tracking-wider text-amber-700">À valider</div>
          <div className="font-serif text-3xl mt-1 text-amber-800">{stats?.by_status?.pending_review ?? 0}</div>
        </Card>
        <Card className="p-4 border-emerald-200">
          <div className="text-xs uppercase tracking-wider text-emerald-700">Validés</div>
          <div className="font-serif text-3xl mt-1 text-emerald-800">{stats?.by_status?.approved ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500">7 derniers jours</div>
          <div className="font-serif text-3xl mt-1">
            {last7dTotal}
          </div>
        </Card>
      </div>
      <Card className="p-5">
        <h3 className="font-serif text-lg mb-3">Répartition par agent</h3>
        <div className="space-y-2">
          {byAgentEntries.map(({ k, n, pct }) => {
            const meta = AGENT_LABEL[k] || { label: k, icon: Bot, color: "text-slate-500" };
            const Icon = meta.icon;
            return (
              <div key={k} className="flex items-center gap-3">
                <Icon className={`w-4 h-4 ${meta.color}`} />
                <span className="text-sm text-slate-700 w-40">{meta.label}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-2">
                  <div className="bg-slate-900 h-2 rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-sm font-medium text-slate-900 w-8 text-right">{n}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );

  const renderAgents = () => (
    <div className="space-y-3" data-testid="agents-section">
      {visibleAgents.map((a) => {
        const meta = AGENT_LABEL[a.name] || { label: a.name, icon: Bot, color: "text-slate-700" };
        const Icon = meta.icon;
        const enabled = a.status === "enabled";
        const busy = busyId === `agent:${a.name}`;
        return (
          <Card key={a.name} className="p-5" data-testid={`agent-row-${a.name}`}>
            <div className="flex items-start gap-4">
              <Icon className={`w-8 h-8 ${meta.color} mt-1 shrink-0`} />
              <div className="flex-1">
                <h3 className="font-serif text-lg flex items-center gap-2">
                  {a.display_name || meta.label}
                  <Badge className={enabled ? "bg-emerald-100 text-emerald-900 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}>
                    {enabled ? "Actif" : "Désactivé"}
                  </Badge>
                </h3>
                <p className="text-sm text-slate-600 mt-1">{a.description}</p>
                <div className="text-xs text-slate-400 mt-2">
                  Cron : toutes les {a.default_schedule_minutes} min · Dernier run : {fmt(a.last_run)}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button onClick={() => runAgent(a.name)} disabled={busy} variant="outline" data-testid={`run-${a.name}`}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                  Exécuter
                </Button>
                <Button onClick={() => toggleAgent(a.name, enabled)} disabled={busy}
                  variant={enabled ? "outline" : "default"}
                  data-testid={`toggle-${a.name}`}>
                  {enabled ? "Désactiver" : "Activer"}
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );

  const renderContents = () => (
    <div className="space-y-4" data-testid="contents-section">
      {/* Filter status */}
      <div className="flex flex-wrap gap-2">
        {["", "pending_review", "approved", "rejected", "archived"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            data-testid={`status-filter-${s || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs border ${
              statusFilter === s ? "bg-slate-900 text-white border-slate-900"
                                 : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
            }`}>
            {s ? (STATUS_BADGE[s]?.label || s) : "Tous statuts"}
          </button>
        ))}
      </div>

      {contents.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <Sparkles className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <h3 className="text-lg font-medium text-slate-800">Aucun contenu</h3>
          <p className="text-sm text-slate-500 mt-1">Activez un agent et cliquez "Exécuter" pour en générer.</p>
        </Card>
      ) : contents.map((c) => {
        const meta = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
        const agentMeta = AGENT_LABEL[c.agent] || { label: c.agent, icon: Bot, color: "text-slate-700" };
        const AgentIcon = agentMeta.icon;
        const busy = busyId === c.id;
        const isEditing = editing?.id === c.id;
        return (
          <Card key={c.id} className="p-5 border-slate-200" data-testid={`content-card-${c.id}`}>
            <div className="flex flex-col md:flex-row gap-4">
              {/* Image */}
              {c.has_image && (
                <img
                  src={`${API}/admin/contents/${c.id}/image?v=${imgBust[c.id] || 0}`}
                  alt={c.title}
                  className="w-full md:w-48 h-32 md:h-32 object-cover rounded-lg border border-slate-200 shrink-0"
                  data-testid={`content-image-${c.id}`}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Badge className={meta.c + " border"}>{meta.label}</Badge>
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                    <AgentIcon className={`w-3 h-3 ${agentMeta.color}`} /> {agentMeta.label}
                  </span>
                  {c.theme && (
                    <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{c.theme}</span>
                  )}
                  {c.score && <span className="text-xs text-slate-500">score {c.score}</span>}
                  <span className="text-[10px] text-slate-400 ml-auto">{fmt(c.created_at)} · v{c.version}</span>
                </div>
                {isEditing ? (
                  <>
                    <Input
                      value={editing.title}
                      onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                      className="mb-2"
                      data-testid={`edit-title-${c.id}`}
                    />
                    <Textarea
                      value={editing.body}
                      onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                      rows={6}
                      data-testid={`edit-body-${c.id}`}
                    />
                  </>
                ) : (
                  <>
                    <h3 className="font-medium text-slate-900 leading-snug">{c.title}</h3>
                    <p className="text-sm text-slate-600 mt-1 whitespace-pre-line line-clamp-4">{c.body}</p>
                    {c.hashtags?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {c.hashtags.map((h, i) => (
                          <span key={i} className="text-xs text-[#C9A961]">#{h.replace(/^#/, "")}</span>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {c.rejected_reason && (
                  <div className="mt-2 text-xs text-rose-700">Motif refus : {c.rejected_reason}</div>
                )}

                {/* Actions */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {isEditing ? (
                    <>
                      <Button size="sm" onClick={savePatch} disabled={busy} data-testid={`save-${c.id}`}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                        Enregistrer
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)} data-testid={`cancel-edit-${c.id}`}>Annuler</Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setEditing({ id: c.id, title: c.title, body: c.body })} data-testid={`edit-${c.id}`}>
                        <Edit3 className="w-3 h-3 mr-1" /> Modifier
                      </Button>
                      {c.status === "pending_review" && (
                        <>
                          <Button size="sm" onClick={() => doAction(c.id, "validate")} disabled={busy}
                            className="bg-emerald-600 hover:bg-emerald-700" data-testid={`validate-${c.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Valider
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => {
                            const r = prompt("Motif de refus (optionnel) :");
                            doAction(c.id, "reject", { reason: r || "" });
                          }} disabled={busy} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`reject-${c.id}`}>
                            <XCircle className="w-3 h-3 mr-1" /> Refuser
                          </Button>
                        </>
                      )}
                      {c.status === "approved" && c.agent === "linkedin_acq" && (
                        <Button size="sm" onClick={() => publishLinkedIn(c.id, "organization")}
                          disabled={busy || !linkedinStatus?.connected}
                          className="bg-[#0B3A2A] hover:bg-[#062618]"
                          title={!linkedinStatus?.connected ? "Connectez LinkedIn d'abord" : "Publier sur la Page CONFILEX"}
                          data-testid={`publish-linkedin-${c.id}`}>
                          {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                          Publier sur LinkedIn
                        </Button>
                      )}
                      {c.status === "published" && c.published_url && (
                        <a href={c.published_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#C9A961] hover:underline px-2"
                          data-testid={`published-link-${c.id}`}>
                          <Eye className="w-3 h-3" /> Voir la publication
                        </a>
                      )}
                      <Button size="sm" variant="outline" onClick={() => doAction(c.id, "archive")} disabled={busy} data-testid={`archive-${c.id}`}>
                        <Archive className="w-3 h-3 mr-1" /> Archiver
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => doAction(c.id, c.has_image ? "regenerate-image" : "visual", { visual_type: "illustration" })}
                        disabled={busy} data-testid={`visual-${c.id}`}>
                        {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ImageIcon className="w-3 h-3 mr-1" />}
                        {c.has_image ? "Régénérer visuel" : "Générer visuel"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => doAction(c.id, "regenerate")} disabled={busy} data-testid={`regenerate-${c.id}`}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Nouveau brouillon
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <header className="mb-6" data-testid="agents-ia-header">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Cockpit Admin</p>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900 mt-1">
            Centre de contrôle Agents IA
          </h1>
          <p className="text-slate-600 mt-2 text-base">
            Bibliothèque centralisée des contenus produits par les agents Veille, LinkedIn, SEO. Validation humaine obligatoire avant toute publication externe.
          </p>
        </header>

        {/* Bandeau connexion LinkedIn */}
        <Card className="p-4 mb-6 border-amber-200 bg-amber-50/40" data-testid="linkedin-banner">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Linkedin className="w-6 h-6 text-[#C9A961] shrink-0" />
            <div className="flex-1 min-w-0">
              {!linkedinStatus?.configured ? (
                <div className="text-sm text-slate-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium">LinkedIn non configuré côté serveur.</span>{" "}
                    Renseignez <code className="text-xs bg-white px-1 rounded border">LINKEDIN_CLIENT_SECRET</code> dans le backend.
                  </span>
                </div>
              ) : linkedinStatus?.connected ? (
                <div className="text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 border">Connecté</Badge>
                    <span className="text-slate-700">par {linkedinStatus.connected_by || "—"}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 break-all">
                    {linkedinStatus.organization_urn ? (
                      <>Page Entreprise : <span className="font-mono">{linkedinStatus.organization_urn}</span>
                      {linkedinStatus.organization_id_source === "ui" && (
                        <button onClick={() => setShowOrgInput(true)}
                          className="ml-2 text-[#C9A961] hover:underline"
                          data-testid="linkedin-org-edit-btn">modifier</button>
                      )} · </>
                    ) : (
                      <button onClick={() => setShowOrgInput(true)}
                        className="text-amber-700 hover:underline"
                        data-testid="linkedin-org-add-btn">
                        ⚠ Page Entreprise non configurée — cliquez pour ajouter l'Organization ID
                      </button>
                    )}
                    {linkedinStatus.organization_urn && (
                      <>Scopes : <span className="font-mono">{linkedinStatus.scopes || "—"}</span> · Expire : {fmt(linkedinStatus.expires_at)}</>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-700">
                  <span className="font-medium">LinkedIn non connecté.</span>{" "}
                  Connectez-vous pour publier directement depuis ce cockpit.
                  {linkedinStatus?.expired && (
                    <span className="ml-2 text-amber-700">(token précédent expiré)</span>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              {linkedinStatus?.connected ? (
                <>
                  <Button onClick={() => setShowManualPost(true)} variant="outline" size="sm"
                    data-testid="manual-post-btn">
                    <Edit3 className="w-4 h-4 mr-1" /> Nouveau post
                  </Button>
                  <Button onClick={disconnectLinkedIn} variant="outline" size="sm"
                    disabled={busyId === "linkedin:disconnect"}
                    data-testid="linkedin-disconnect-btn">
                    {busyId === "linkedin:disconnect" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                    Déconnecter
                  </Button>
                </>
              ) : (
                <Button onClick={connectLinkedIn} disabled={!linkedinStatus?.configured || busyId === "linkedin:connect"}
                  className="bg-[#0B3A2A] hover:bg-[#062618]" size="sm"
                  data-testid="linkedin-connect-btn">
                  {busyId === "linkedin:connect" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Linkedin className="w-4 h-4 mr-1" />}
                  Connecter LinkedIn
                </Button>
              )}
            </div>
          </div>

          {/* Inline org-id input */}
          {showOrgInput && (
            <div className="mt-3 pt-3 border-t border-amber-100 flex flex-wrap items-end gap-2"
              data-testid="linkedin-org-input-row">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">
                  Organization ID (numérique)
                </label>
                <Input
                  value={orgInput}
                  onChange={(e) => setOrgInput(e.target.value)}
                  placeholder="ex : 108234567"
                  data-testid="linkedin-org-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Trouvable dans l'URL admin de votre Page LinkedIn : linkedin.com/company/<strong>[NUMÉRO]</strong>/admin/
                </p>
              </div>
              <Button onClick={saveOrgId} disabled={busyId === "linkedin:org"}
                className="bg-emerald-600 hover:bg-emerald-700" size="sm"
                data-testid="linkedin-org-save">
                {busyId === "linkedin:org" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Enregistrer
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setShowOrgInput(false); setOrgInput(""); }}>
                Annuler
              </Button>
            </div>
          )}
        </Card>

        {/* Modal post manuel */}
        {showManualPost && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowManualPost(false)}
            data-testid="manual-post-modal">
            <Card className="bg-white max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-serif text-xl mb-1">Créer un post manuel</h3>
              <p className="text-sm text-slate-600 mb-4">Pour publier en réaction à une actualité urgente, sans attendre l'agent IA.</p>

              <div className="space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Type</label>
                  <select
                    value={manualPost.content_type}
                    onChange={(e) => setManualPost({ ...manualPost, content_type: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    data-testid="manual-post-type">
                    <option value="linkedin_post">Post LinkedIn (court)</option>
                    <option value="linkedin_long">Article LinkedIn (long)</option>
                    <option value="linkedin_authority">Post d'autorité</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Titre / Accroche</label>
                  <Input
                    value={manualPost.title}
                    onChange={(e) => setManualPost({ ...manualPost, title: e.target.value })}
                    placeholder="Titre du post"
                    data-testid="manual-post-title"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Corps du post</label>
                  <Textarea
                    value={manualPost.body}
                    onChange={(e) => setManualPost({ ...manualPost, body: e.target.value })}
                    placeholder="Texte complet du post (3000 caractères max sur LinkedIn)"
                    rows={8}
                    data-testid="manual-post-body"
                  />
                  <p className="text-xs text-slate-500 mt-1">{manualPost.body.length} / 3000 caractères</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Thème (optionnel)</label>
                    <Input
                      value={manualPost.theme}
                      onChange={(e) => setManualPost({ ...manualPost, theme: e.target.value })}
                      placeholder="ex : Fiscalité"
                      data-testid="manual-post-theme"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Hashtags (séparés par virgule)</label>
                    <Input
                      value={manualPost.hashtags}
                      onChange={(e) => setManualPost({ ...manualPost, hashtags: e.target.value })}
                      placeholder="ex : confilex, fiscalite"
                      data-testid="manual-post-hashtags"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={manualPost.auto_approve}
                    onChange={(e) => setManualPost({ ...manualPost, auto_approve: e.target.checked })}
                    data-testid="manual-post-auto-approve"
                    className="w-4 h-4"
                  />
                  Marquer immédiatement comme validé (prêt à publier)
                </label>
              </div>

              <div className="flex justify-end gap-2 mt-5">
                <Button variant="outline" onClick={() => setShowManualPost(false)} data-testid="manual-post-cancel">
                  Annuler
                </Button>
                <Button onClick={submitManualPost} disabled={busyId === "manual:post"}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="manual-post-submit">
                  {busyId === "manual:post" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  Créer le post
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="agents-ia-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setStatusFilter(""); }}
                data-testid={`tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition ${
                  tab === t.key
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                }`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {loading && !contents.length ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : (
          tab === "overview" ? renderOverview()
          : tab === "agents" ? renderAgents()
          : renderContents()
        )}
      </main>
      <Footer />
    </div>
  );
}
